import { useEffect, useState } from 'react'
import { supabase } from './supabaseClient'

const formatJobOptionLabel = (job) => {
  const jobType = String(job?.job_type || '').trim() || 'Untitled Job'
  const jobDate = String(job?.job_date || '').trim()
  return jobDate ? `${jobType} - ${jobDate}` : jobType
}

export default function EmployeeClockIn({ allowWorkerSelection = false }) {
  const [workers, setWorkers] = useState([])
  const [workerId, setWorkerId] = useState('')
  const [jobs, setJobs] = useState([])
  const [jobId, setJobId] = useState('')
  const [jobsLoadError, setJobsLoadError] = useState('')
  const [clockIn, setClockIn] = useState('')
  const [clockOut, setClockOut] = useState('')
  const [message, setMessage] = useState('')

  useEffect(() => {
    let active = true
    const loadFormDependencies = async () => {
      const workersPromise = allowWorkerSelection
        ? supabase.from('workers').select('id, name').order('name')
        : Promise.resolve({ data: [], error: null })
      const jobsPromise = supabase.from('jobs').select('id, job_type, job_date').order('job_date', { ascending: false }).limit(200)
      const [workersResponse, jobsResponse] = await Promise.all([workersPromise, jobsPromise])

      if (!active) return

      const workerRows = workersResponse.data || []
      setWorkers(workerRows)
      if (allowWorkerSelection) {
        setWorkerId((currentWorkerId) => {
          if (currentWorkerId && workerRows.some((worker) => String(worker.id) === String(currentWorkerId))) {
            return currentWorkerId
          }
          return workerRows[0]?.id ? String(workerRows[0].id) : ''
        })
      }

      if (jobsResponse.error) {
        setJobs([])
        setJobId('')
        setJobsLoadError(jobsResponse.error.message || 'Unable to load jobs.')
        return
      }

      const jobRows = jobsResponse.data || []
      setJobs(jobRows)
      setJobsLoadError('')
      setJobId((currentJobId) => {
        if (currentJobId && jobRows.some((job) => String(job.id) === String(currentJobId))) {
          return currentJobId
        }
        return ''
      })
    }
    loadFormDependencies()
    return () => {
      active = false
    }
  }, [allowWorkerSelection])

  const jobsBlockedMessage = jobsLoadError
    ? `Unable to load jobs for manual punch entry: ${jobsLoadError}`
    : jobs.length === 0
      ? 'No jobs available for manual punch entry. Add a job in Calendar first.'
      : ''

  const isSubmitDisabled = Boolean(jobsBlockedMessage)

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (jobsBlockedMessage) {
      setMessage(jobsBlockedMessage)
      return
    }

    if (!workerId || !jobId || !clockIn) {
      setMessage('Worker, job, and clock-in are required.')
      return
    }

    const clockInDate = new Date(clockIn)
    if (Number.isNaN(clockInDate.getTime())) {
      setMessage('Clock-in time is invalid.')
      return
    }

    let clockOutDate = null
    if (clockOut) {
      clockOutDate = new Date(clockOut)
      if (Number.isNaN(clockOutDate.getTime())) {
        setMessage('Clock-out time is invalid.')
        return
      }
      if (clockOutDate.getTime() < clockInDate.getTime()) {
        setMessage('Clock-out must be after clock-in.')
        return
      }
    }

    const payload = {
      worker_id: workerId,
      job_id: jobId,
      entry_date: String(clockIn).slice(0, 10),
      clocked_in_at: clockInDate.toISOString(),
      clocked_out_at: clockOutDate ? clockOutDate.toISOString() : null,
      manual_adjustment: true,
      adjustment_reason: 'manual_entry'
    }

    const { error } = await supabase.from('employee_time_entries').insert([payload])
    if (error) {
      setMessage('Unable to save manual punch entry: ' + error.message)
      return
    }

    setMessage('Manual punch entry saved.')
    setJobId('')
    setClockIn('')
    setClockOut('')
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 8 }}>
      {allowWorkerSelection ? (
        <label>
          Worker
          <select value={workerId} onChange={(event) => setWorkerId(event.target.value)}>
            <option value="">Select worker</option>
            {workers.map((worker) => (
              <option key={worker.id} value={worker.id}>
                {worker.name || worker.id}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <label>
        Job
        <select value={jobId} onChange={(event) => setJobId(event.target.value)}>
          <option value="">Select job</option>
          {jobs.map((job) => (
            <option key={job.id} value={job.id}>
              {formatJobOptionLabel(job)}
            </option>
          ))}
        </select>
      </label>
      <label>
        Clock In
        <input type="datetime-local" value={clockIn} onChange={(event) => setClockIn(event.target.value)} />
      </label>
      <label>
        Clock Out
        <input type="datetime-local" value={clockOut} onChange={(event) => setClockOut(event.target.value)} />
      </label>
      {jobsBlockedMessage ? <p>{jobsBlockedMessage}</p> : null}
      <button type="submit" disabled={isSubmitDisabled}>Save Manual Punch</button>
      {message ? <p>{message}</p> : null}
    </form>
  )
}
